/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePeriod } from '@/pages/reports/PeriodContext';
import { computeSavingsRate, computeMoMChange } from '@/lib/analytics/portfolioSummary';
import { PortfolioSummary } from '@/lib/analytics/types';

/** Returns the first and last day of the calendar month prior to the given date string. */
function previousMonthRange(dateFrom: string): { start: string; end: string } {
  const d = new Date(dateFrom);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed; this is the month BEFORE dateFrom's month
  const lastDay = new Date(year, month, 0).getDate();
  const prevMonth = month === 0 ? 12 : month;
  const prevYear = month === 0 ? year - 1 : year;
  const mm = String(prevMonth).padStart(2, '0');
  const dd = String(lastDay).padStart(2, '0');
  return {
    start: `${prevYear}-${mm}-01`,
    end: `${prevYear}-${mm}-${dd}`,
  };
}

/** Returns the date 12 months before the given ISO date string. */
function twelveMonthsAgo(dateStr: string): string {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

export function usePortfolioSummary() {
  const { user } = useAuth();
  const { period } = usePeriod();

  const prevRange = previousMonthRange(period.dateFrom);
  const netWorthStart = twelveMonthsAgo(period.dateTo);

  return useQuery({
    queryKey: ['portfolio-summary', user?.id, period.dateFrom, period.dateTo],
    queryFn: async (): Promise<PortfolioSummary> => {
      // Fetch current period transactions
      const { data: currentTx, error: currentErr } = await (supabase as any)
        .from('transactions')
        .select('amount, currency, type')
        .eq('user_id', user!.id)
        .gte('transaction_date', period.dateFrom)
        .lte('transaction_date', period.dateTo);

      if (currentErr) throw currentErr;

      const txList = currentTx ?? [];
      const totalIncome = txList
        .filter((t: any) => t.type === 'income')
        .reduce((sum: number, t: any) => sum + t.amount, 0);
      const totalExpenses = txList
        .filter((t: any) => t.type === 'expense')
        .reduce((sum: number, t: any) => sum + t.amount, 0);

      const netSavings = totalIncome - totalExpenses;
      const savingsRate = totalIncome > 0 ? computeSavingsRate(totalIncome, totalExpenses) : 0;

      // Fetch previous month expenses for MoM change
      const { data: prevTx, error: prevErr } = await (supabase as any)
        .from('transactions')
        .select('amount, currency, type')
        .eq('user_id', user!.id)
        .eq('type', 'expense')
        .gte('transaction_date', prevRange.start)
        .lte('transaction_date', prevRange.end);

      if (prevErr) throw prevErr;

      const prevExpenses = (prevTx ?? []).reduce((sum: number, t: any) => sum + t.amount, 0);
      const momExpenseChange = prevExpenses > 0 ? computeMoMChange(totalExpenses, prevExpenses) : 0;

      // Fetch latest net worth snapshot for total account balance
      const { data: nwSnapshots, error: nwErr } = await (supabase as any)
        .from('net_worth_snapshots')
        .select('net_worth, currency, snapshot_date')
        .eq('user_id', user!.id)
        .gte('snapshot_date', netWorthStart)
        .lte('snapshot_date', period.dateTo)
        .order('snapshot_date', { ascending: false })
        .limit(1);

      if (nwErr) throw nwErr;

      const totalAccountBalance = nwSnapshots?.[0]?.net_worth ?? 0;

      return {
        totalIncome,
        totalExpenses,
        netSavings,
        savingsRate,
        momExpenseChange,
        totalAccountBalance,
        currency: nwSnapshots?.[0]?.currency || 'KES',
      };
    },
    enabled: !!user,
  });
}
